/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import useSWR from 'swr';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { OTPInput } from 'Components/OTPInput';
import { encryptedKeyForCredentials } from 'Commons';
import { Pathname } from 'Routes';
import { Button } from 'Components';
import { AppContext, AuthContext } from 'Context';
import { AxiosCancelToken, dataFetcher, endpoints } from 'Api';
import { createRecaptchaVerifier, sendFirebaseOtp, mapFirebaseAuthError, phoneAuthSession } from 'Api/firebaseAuthClient';
import LightRays from 'Components/reactbits/LightRays';
import RotatingLoader from 'Components/RotatingLoader';
import { useDispatch } from 'react-redux';
import { clearAllDummys } from 'store/store';
import { LogOut } from 'Utils/lucideIcons';
import { isMobile } from 'react-device-detect';
import { AuthPage } from '../components/page';
import { Snackbar } from '../../../../Components/index';
import theme from '../components/auth-theme.module.css';
import styles from './VerifyCodeClassic.module.css';

/**
 * Classic (pre-redesign) OTP screen. Markup from feature/affiliate-membership
 * (latest old UI, which includes the Firebase OTP integration); the logic above
 * is the current branch's.
 */
export const VerifyCodeClassic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setAppSnackbar,
    userDetails,
    setUserDetails,
    plansPage,
    setPlansPage,
    langText,
    setReferralStatus,
  } = useContext(AppContext);
  const {
    forceAutoSignin,
    setToken,
    getToken,
    setEmailVerified,
    removeToken,
    setUserSignInStatus,
    removeEmailVerified,
  } = useContext(AuthContext);
  const dispatch = useDispatch();
  const isDesktopApp = sessionStorage.getItem('isDesktopApp') === 'true';
  const mobileVerificationText = langText?.mobileVerification;
  const headerText = langText?.header;
  const commonText = langText?.common;
  const signUpText = langText?.signInUp;
  const OTP_RESEND_TIME = 60;
  const [expireTime, setExpireTime] = useState(OTP_RESEND_TIME);
  const [OTP, setOTP] = useState({
    one: '',
    two: '',
    three: '',
    four: '',
    five: '',
    six: '',
  });
  const [otpFinal, setOtpFinal] = useState();
  // const [OTP, setOTP] = useState('');
  const email = sessionStorage.getItem(encryptedKeyForCredentials.email);
  // const mobile = sessionStorage.getItem(encryptedKeyForCredentials.mobile);
  const password = sessionStorage.getItem(encryptedKeyForCredentials.password);
  const [mobile, setMobile] = useState(
    sessionStorage.getItem(encryptedKeyForCredentials.mobile)
  );
  // The code inputs are always on screen. Every signal this used to be gated on
  // is unreliable on a remount — the session flag is one-shot (an effect below
  // deletes it on the first mount) and the Firebase confirmation session lives
  // in memory only — so pressing Back from the change-number screen could land
  // here with all of them gone and render nothing but the Resend/Change
  // buttons. There is no state where hiding the inputs helps: either a code is
  // already out, or the auto-send below is fetching one behind the loader.
  // `setOtpReached` is kept because the send paths still call it.
  const [isOtpReached, setOtpReached] = useState(true);
  const [first, setfirst] = useState(true);
  const [sendOtpCount, setSendOtpCount] = useState(0);
  const [pastedValue, setPastedValue] = useState('');
  const token = getToken();
  const firstInput = useRef();
  const secondInput = useRef();
  const thirdInput = useRef();
  const fourthInput = useRef();
  const fifthInput = useRef();
  const sixthInput = useRef();
  const re = /^[0-9\b]+$/;
  const [confirmingCode, setConfirmingCode] = useState(false);

  // Everyone who reaches this screen is mid-signup, so there is no second
  // destination to choose between. The gate that sends users here fires on
  // `password` being set and `verify_mobile` not being 1, and OTP verification
  // is what sets `verify_mobile` — so a user who hasn't passed it cannot have
  // reached the avatar or occasion steps that come after it. Every confirmed
  // code therefore continues into avatar; nothing about the browser it happened
  // in is consulted, which is what makes it work on a second device too.

  // Registration/login is already done — the user must stay on this screen
  // until the code is confirmed. On mount we push one spare history entry
  // pointing at the same URL, so the first Back press pops that spare instead
  // of the signup page behind it — and because both entries share a URL,
  // react-router re-renders nothing and there's no flash. The popstate handler
  // then re-pushes the spare, so Back never gets anywhere no matter how often
  // it is pressed. That covers the browser button, the swipe-back gesture and
  // the device hardware button (WebView wrappers turn it into history.back());
  // Cordova-style wrappers emitting `backbutton` are handled explicitly.
  useEffect(() => {
    const lockedUrl = window.location.href;
    const pinCurrentEntry = () => {
      window.history.pushState({ navigationLock: true }, '', lockedUrl);
    };
    pinCurrentEntry();
    const handlePopState = () => { pinCurrentEntry(); };
    const handleDeviceBack = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('backbutton', handleDeviceBack);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('backbutton', handleDeviceBack);
    };
  }, []);

  // The lock above leaves a spare history entry behind, so Back from the screen
  // we hand off to can land here again. If verification already finished in
  // this session, bounce forward instead of asking for a second code.
  useEffect(() => {
    const completedPath = sessionStorage.getItem('otpFlowCompletedPath');
    if (completedPath) {
      navigate(completedPath, { replace: true });
    }
  }, []);

  const { isValidating: signingInUser, mutate: signInUser } = useSWR([endpoints.signin, email, password], {
    fetcher: (url, inputEmail, inputPassword) => dataFetcher(url, { email: inputEmail, password: inputPassword }),
    onSuccess: ({ isNewUser, success, data, api_response, referral_status, is_skip, message, verify_mobile, mobile: userMobile }) => {
      if (success) {
        if (window.gtag) {
        window.gtag('event', 'sign_up', {
          method: 'email', // or 'google', etc., based on your signup method
          user_id: data?.id,
          email: data?.email,
          timestamp: new Date().toISOString(), // GA will treat this as a custom param
        });
      }

        // Handle desktop app callback - this is where signup/login actually completes
        if (isDesktopApp) {
          const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
          const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
          const callbackUrl = `${callbackScheme}?${new URLSearchParams({
            token: api_response?.access || '',
            projector_token: data?.token || '',
            email: data?.email || email,
            isNewUser
          }).toString()}`;

          // Clean up session storage
          sessionStorage.removeItem('isDesktopApp');
          sessionStorage.removeItem('isWindowsDesktop');
          sessionStorage.removeItem(encryptedKeyForCredentials.email);
          sessionStorage.removeItem(encryptedKeyForCredentials.password);
          sessionStorage.removeItem(encryptedKeyForCredentials.mobile);

          window.location.href = callbackUrl;
          return;
        }

        sessionStorage.removeItem(encryptedKeyForCredentials.email);
        sessionStorage.removeItem(encryptedKeyForCredentials.password);
        sessionStorage.removeItem(encryptedKeyForCredentials.mobile);
        setUserDetails({ ...userDetails, isNewUser, mobile: userMobile });
        setMobile(userMobile);
        setToken(data?.token);
        setEmailVerified(data?.is_email_verified);
        // setAppSnackbar({ message: 'Logged in successfully', isVisible: true });
        forceAutoSignin();
        if ((parseInt(referral_status, 10) === 1 && parseInt(is_skip, 10) !== 1 && !data?.billing_date)) {
          setPlansPage(false);
          parseInt(referral_status, 10) === 1 && parseInt(is_skip, 10) !== 1 ? setReferralStatus(1) : setReferralStatus(0);
           navigate(Pathname.plans);
        } else if (parseInt(data?.verify_mobile, 10) === 0) {
          // has_logged_in === '0' ? navigate(Pathname.gettingStarted) : navigate(Pathname.access);
          navigate(Pathname.authentication.newmobileVerification);
        } else {
          // setAppSnackbar({
          //   message: message || 'Incorrect password',
          //   isVisible: true,
          // });
        }
      } else if (!verify_mobile) {
        if (userMobile) {
          //   navigate(Pathname.authentication.verifyCode);
          navigate(Pathname.authentication.newverifyCode);
        } else {
          navigate(Pathname.authentication.newmobileVerification);
        }
      } else {
        if (isDesktopApp) {
          const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
          const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
          window.location.href = `${callbackScheme}?error=${encodeURIComponent(message || 'Verification failed')}`;
          return;
        }
        setAppSnackbar({ message: message || 'Incorrect password', isVisible: true });
      }
      navigate(Pathname.authentication.avatar, { state: { fromVerificationCode: true } });
    },
    onError: (error) => {
      if (isDesktopApp) {
        const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
        const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
        window.location.href = `${callbackScheme}?error=${encodeURIComponent(error?.response?.data?.message || 'Authentication failed')}`;
        return;
      }
      const responseData = error?.response?.data;
      // if ('email_verified' in responseData && !responseData?.email_verified) {
      //   sessionStorage.setItem('emailForValidation_projectoruser', email);
      //   navigate(Pathname.authentication.verifyEmail);
      // } else {
      setAppSnackbar({ message: responseData?.message || 'Oops! Something went wrong', isVisible: true });
      setConfirmingCode(false);
      // }
    },
  });
  // Shared "code accepted" action. The destination is always the avatar step —
  // only how we get a session there differs:
  //
  //   - Signup lane: the credentials are still in session storage, so sign in
  //     with them (that call's onSuccess lands on avatar itself).
  //   - Gate lane: a user pushed here by the app-load check is already holding a
  //     token — that token is how their profile was read in the first place — but
  //     their password was never in session storage, so signing in is impossible
  //     and unnecessary. Refresh the profile (verify_mobile is now 1) and go.
  const proceedAfterOtp = () => {
    // Sent here mid-onboarding (the avatar step's mobile check) — the account is
    // already signed in, so skip the signin round-trip and resume that lane.
    if (location.state?.nextPath) {
      navigate(location.state.nextPath, { replace: true });
      return;
    }
    sessionStorage.setItem('otpFlowCompletedPath', Pathname.authentication.avatar);
    if (email && password) {
      signInUser();
      return;
    }
    forceAutoSignin();
    navigate(Pathname.authentication.avatar, { state: { fromVerificationCode: true }, replace: true });
  };

  const defaultSnackbar = { isVisible: false, message: '', type: 'default' };
  const [snackbarData, setSnackbar] = useState(defaultSnackbar);
  const resendRecaptchaVerifierRef = useRef(null);
  const idTokenRef = useRef();
  // Seeded from the same condition the auto-send effect below tests, so the
  // very first paint already shows the loader. Otherwise the effect (which only
  // runs after that paint) would leave a frame showing the bare Resend button
  // with no code boxes — which is what the user sees on the login route.
  const [sendingOtp, setSendingOtp] = useState(
    () => !phoneAuthSession.confirmationResult
      && Boolean(sessionStorage.getItem(encryptedKeyForCredentials.mobile))
  );

  const { isValidating: verifyingToken, mutate: verifyFirebaseToken } = useSWR([endpoints.verifyOtpFirebase, email], {
    fetcher: (url, email) => dataFetcher(url, { email, firebaseToken: idTokenRef.current }),
    onSuccess: ({ success, message }) => {
      if (success) {
        proceedAfterOtp();
      } else if (isDesktopApp) {
        const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
        const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
        window.location.href = `${callbackScheme}?error=${encodeURIComponent(message || 'OTP verification failed')}`;
      } else {
        setAppSnackbar({ message: message || 'Oops! Something went wrong', isVisible: true });
        setConfirmingCode(false);
      }
    },
    onError: (error) => {
      if (isDesktopApp) {
        const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
        const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
        window.location.href = `${callbackScheme}?error=${encodeURIComponent(error?.response?.data?.message || 'OTP verification failed')}`;
      } else {
        const responseData = error?.response?.data;
        setSnackbar({ isVisible: true, message: responseData?.message, type: 'error' });
        setAppSnackbar({ message: responseData?.message || 'Oops! Something went wrong', isVisible: true });
        setConfirmingCode(false);
      }
    },
  });
  const verifyingOTP = confirmingCode || verifyingToken || signingInUser;
  // Any in-flight work — sending the first code, resending, or confirming one —
  // blocks the whole screen rather than leaving a half-built page behind.
  const isBusy = sendingOtp || verifyingOTP;

  // Stays `true` from the moment the user submits until either the flow fails
  // (each failure path re-enables the button so the code can be retried) or we
  // navigate away — this avoids a gap between the Firebase confirm resolving
  // and the backend calls starting, where the button would briefly re-enable.
  const verifyOtp = async () => {
    // The confirmation session lives in memory only, so it can be gone while a
    // code the user typed is still on screen (reload, or a trip to the
    // change-number screen and back). Point them at Resend rather than throwing
    // on a null session.
    if (!phoneAuthSession.confirmationResult) {
      setAppSnackbar({ message: 'That code has expired. Please tap Resend Code.', isVisible: true });
      // Clear the cooldown so Resend is available right now.
      setExpireTime(0);
      return;
    }
    setConfirmingCode(true);
    try {
      const result = await phoneAuthSession.confirmationResult.confirm(otpFinal);
      idTokenRef.current = await result.user.getIdToken();
      verifyFirebaseToken();
    } catch (error) {
      setAppSnackbar({ message: mapFirebaseAuthError(error), isVisible: true });
      setConfirmingCode(false);
    }
  };

  const getResendRecaptchaVerifier = () => {
    if (!resendRecaptchaVerifierRef.current) {
      resendRecaptchaVerifierRef.current = createRecaptchaVerifier('firebase-recaptcha-container-resend');
    }
    return resendRecaptchaVerifierRef.current;
  };

  const requestVerificationCode = async () => {
    setSendingOtp(true);
    try {
      const verifier = getResendRecaptchaVerifier();
      await sendFirebaseOtp(mobile, verifier);
      setOtpReached(true);
      setSendOtpCount(1);
      setAppSnackbar({ message: 'Code send to mobile number', isVisible: true });
    } catch (error) {
      setAppSnackbar({ message: mapFirebaseAuthError(error), isVisible: true });
      resendRecaptchaVerifierRef.current = null;
      // Nothing was sent, so don't make the user sit out the cooldown — Resend
      // is their only way forward now that Back is locked.
      setExpireTime(0);
    } finally {
      setSendingOtp(false);
    }
  };

  // A user sent here by the post-login gate (or one who reloaded the page) has
  // no live Firebase confirmation session, so any code already in their inbox
  // can't be confirmed. Send a fresh one automatically — the login flow then
  // needs no send step of its own, and the screen is never a dead end.
  const autoSendAttempted = useRef(false);
  useEffect(() => {
    if (autoSendAttempted.current) return;
    if (phoneAuthSession.confirmationResult) return;
    if (!mobile) return;
    autoSendAttempted.current = true;
    setExpireTime(OTP_RESEND_TIME);
    requestVerificationCode();
  }, [mobile]);

  // Session storage can come back empty on a remount, which used to leave the
  // auto-send above with no number to send to — and so no code, no loader, and
  // a screen the user can't act on. The signed-in profile knows the number, so
  // fall back to it; setting state here re-runs the effect above.
  useEffect(() => {
    if (!mobile && userDetails?.mobile) {
      setMobile(userDetails.mobile);
    }
  }, [mobile, userDetails?.mobile]);

  useEffect(() => {
    if (isOtpReached) {
      if (!expireTime) return;
      const intervalId = setInterval(() => {
        setExpireTime(expireTime - 1);
      }, 1000);
      // clear interval on re-render to avoid memory leaks
      return () => clearInterval(intervalId);
    }
  }, [expireTime, isOtpReached]);

  useEffect(() => {
    if (!sendOtpCount && !isOtpReached) {
      // requestVerificationCode();
    } else {
      setOtpReached(true);
      sessionStorage.removeItem('#fs656gfhllh@#$');
    }
  }, [sendOtpCount, isOtpReached]);

  useEffect(() => {
    setOtpFinal(`${OTP.one}${OTP.two}${OTP.three}${OTP.four}${OTP.five}${OTP.six}`);
  }, [OTP]);

  /** Change state of input according to pasted data */
  useEffect(() => {
    if (pastedValue.length === 6) {
      setOTP({
        one: pastedValue[0],
        two: pastedValue[1],
        three: pastedValue[2],
        four: pastedValue[3],
        five: pastedValue[4],
        six: pastedValue[5],
      });
      setPastedValue('');
      sixthInput?.current.focus();
    }
  }, [pastedValue]);

  const resendCodeHandler = () => {
    setExpireTime(OTP_RESEND_TIME);
    setSendOtpCount(0);
    requestVerificationCode();
  };

  function handleOtpInput(value, otpInputPos, nextInput, prevInput) {
    if (value && value !== '' && nextInput) {
      nextInput?.current.focus();
    } else if ((!value || value === '') && prevInput) {
      prevInput?.current.focus();
    }
    if (value?.length <= 1) {
      setOTP({ ...OTP, [otpInputPos]: value });
    }
  }

  const handlePaste = (event) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text');
    if (paste.length <= 6) {
      setPastedValue(paste);
    }
  };

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Mirrors the header's Log Out: same cookie/localStorage/sessionStorage
  // clearing, same context resets, same redux reset. The header additionally
  // cancels in-flight uploads, which can't exist for a user who hasn't got
  // past verification yet.
  //
  // Leaves via a full page load rather than a router navigation: it wipes the
  // in-memory Firebase confirmation session along with everything else, and
  // `replace` drops this screen (and the spare entry its back-lock pushed) from
  // the history stack so Back can't return to a half-dead OTP page.
  const logout = () => {
    document.cookie.split('; ').forEach((cookie) => {
      const [name] = cookie.split('=');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
    localStorage.removeItem('token');
    localStorage.removeItem('uploader_token');
    localStorage.removeItem('uploader_token_expiry');
    localStorage.removeItem('user_uuid');
    localStorage.removeItem('dismissedPaymentDialog');
    localStorage.removeItem('cardEXPDialog');
    sessionStorage.clear();
    if (window.caches) {
      window.caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          window.caches.delete(cacheName);
        });
      });
    }
    removeToken();
    setUserSignInStatus(false);
    removeEmailVerified();
    dispatch(clearAllDummys());
    window.location.replace(Pathname.authentication.newsignIn);
  };

  const handleCancel = () => {
    if (isDesktopApp) {
      const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
      const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
      window.location.href = `${callbackScheme}?error=cancelled`;
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <div>
        <AuthPage>
          {
            !isMobile && <div aria-hidden="true" onClick={() => { navigate(-1); }} className={styles.backLink}>BACK</div>
          }
          {
            isDesktopApp && <div style={{ marginTop: '50px' }} aria-hidden="true" onClick={handleCancel} className={styles.backLink}>Cancel</div>
          }
          {isOtpReached ? (
            <form
              onPaste={handlePaste}
              className={styles.alignForm}
              onSubmit={(e) => {
                e.preventDefault();
                verifyOtp();
              }}
            >
              <div className={styles.inputLabel}>
                {mobileVerificationText?.enterYourCode || 'Enter your code'}
              </div>
              <div className={styles.description}>
                <div>
                  A code was send to your number, please check and enter below.
                </div>
                <div>
                  If you do not see the code please try to resend in &nbsp;
                  <span style={{ color: 'white' }}>{`${expireTime}s`}</span>
                </div>
              </div>
              {/* <span style={{ textAlign: 'center' }} className={styles.description}>{`A code was send to your number ${mobile || userDetails?.mobile}, please check and enter below. If you do not see the code please try to resend in `}</span>
                <span className={styles.expireTime}>{`${expireTime}s`}</span> */}
              <br />
              <div className={styles.buttonBlock}>
                <div style={{ paddingLeft: '8px', display: 'flex' }}>
                  <div style={{ paddingRight: '18px' }}>
                    <input
                      type="number"
                      max={9}
                      value={OTP.one}
                      ref={firstInput}
                      onChange={(event) => handleOtpInput(event.target.value, 'one', secondInput)}
                      className={styles.codeInput}
                    />
                  </div>
                  <div style={{ paddingRight: '18px' }}>
                    <input
                      type="number"
                      max={9}
                      value={OTP.two}
                      ref={secondInput}
                      onChange={(event) => handleOtpInput(
                          event.target.value,
                          'two',
                          thirdInput,
                          firstInput
                        )}
                      className={styles.codeInput}
                    />
                  </div>
                  <div style={{ paddingRight: '18px' }}>
                    <input
                      type="number"
                      max={9}
                      value={OTP.three}
                      ref={thirdInput}
                      onChange={(event) => handleOtpInput(
                          event.target.value,
                          'three',
                          fourthInput,
                          secondInput
                        )}
                      className={styles.codeInput}
                    />
                  </div>
                  <div style={{ paddingRight: '18px' }}>
                    <input
                      type="number"
                      max={9}
                      value={OTP.four}
                      ref={fourthInput}
                      onChange={(event) => handleOtpInput(
                          event.target.value,
                          'four',
                          fifthInput,
                          thirdInput
                        )}
                      className={styles.codeInput}
                    />
                  </div>
                  <div style={{ paddingRight: '18px' }}>
                    <input
                      type="number"
                      max={9}
                      value={OTP.five}
                      ref={fifthInput}
                      onChange={(event) => handleOtpInput(
                          event.target.value,
                          'five',
                          sixthInput,
                          fourthInput
                        )}
                      className={styles.codeInput}
                    />
                  </div>
                  <div style={{ paddingRight: '18px' }}>
                    <input
                      type="number"
                      max={9}
                      value={OTP.six}
                      ref={sixthInput}
                      onChange={(event) => handleOtpInput(
                          event.target.value,
                          'six',
                          null,
                          fifthInput
                        )}
                      className={styles.codeInput}
                    />
                  </div>
                </div>
                {/* <input
                    type="tel"
                    maxLength={5}
                    value={OTP}
                    onChange={handleChange}
                    style={{ width: '200px', height: '55px', borderRadius: '10px', backgroundColor: '#31343e', border: '2px solid #6d6f76', color: '#ffffff', fontSize: '20px', fontWeight: '700', textAlign: 'center' }}
                  /> */}
                {/* <OTPInput className={styles.otpInputs} inputStyles={{ width: '60px', height: '55px', borderRadius: '10px', backgroundColor: '#31343e', border: '2px solid #6d6f76', color: '#ffffff', fontSize: '20px', fontWeight: '700' }} value={OTP} onChange={setOTP} autoFocus OTPLength={5} otpType="number" disabled={false} /> */}
              </div>
              <br />
              <div className={styles.buttonBlock}>
                <Button
                  isFullWidth
                  blockText
                  isDisabled={
                    !otpFinal || otpFinal?.length !== 6 || verifyingOTP
                  }
                  isBlue
                  isLarge
                  submit
                >
                  {mobileVerificationText?.continue || 'Continue'}
                </Button>
              </div>
            </form>
          ) : null}
          <div
            className={styles.resendDiv}
            // onClick={() => { setExpireTime(120); setSendOtpCount(0); }}
          >
            <button
              style={{ borderStyle: 'none', marginTop: '-0px' }}
              onClick={() => {
                resendCodeHandler();
              }}
              disabled={expireTime !== 0 || sendingOtp}
            >
              {mobileVerificationText?.resendLink || 'Resend Code'}
            </button>
          </div>
          <Snackbar
            {...snackbarData}
            onClose={() => {
              setSnackbar({ ...snackbarData, isVisible: false });
            }}
          />
          <div id="firebase-recaptcha-container-resend" />
        </AuthPage>
      </div>
    </>
  );
};

export default VerifyCodeClassic;
